// src/layouts/SideDrawer.js
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  IconButton,
  Box,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  Psychology as CompetenciasIcon,
  Work as CargosIcon,
  Summarize as ReportsIcon,
  EmojiPeople as ApplicantsIcon,
  CompareArrows as CompareArrowsIcon,
  Assignment as AssignmentIcon,
  Home as HomeIcon,
  AccountCircle as DiscIcon,
  Quiz as ReasoningIcon,
  SettingsAccessibility as PersonalHabilitiesIcon,
  FilterAlt as FilterAltIcon,
  TrendingUp as SalesIcon,
  Groups as MassiveRecruitmentIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  WorkOutline as AtsPositionsIcon,
  PersonSearch as AtsCandidatesIcon,
  AssignmentInd as AtsApplicationsIcon,
  Business as ErpClientsIcon,
  RequestQuote as ErpQuotationsIcon,
  ContactPage as ErpEmployeesIcon,
  Article as ErpContractsIcon,
  FileDownload as ErpExportIcon,
  TravelExplore as HuntingSearchIcon,
  Storefront as AtsFairsIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useStore from '../config/store';
import { canSeeErpSection } from '../erp/erpAccess';
import { canUseAssessment2 } from '../assessment2/assessment2Access'; // Assessment 2 (PLAN_ASSESSMENT2.md)

const drawerWidthExpanded = 300;
const drawerWidthCollapsed = 64;

const SideDrawer = ({ isCollapsed, onToggleDrawer }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { plan, user, appMode, erpRole } = useStore();

  const isSuperAdmin = user?.rol === 'super-admin';
  const assessment2Organization = useStore((s) => s.organization); // Assessment 2 (PLAN_ASSESSMENT2.md)

  const allMenuOptions = [
    {
      label: 'Home',
      path: '/home',
      icon: <HomeIcon />,
      allowedForFree: true,
      allowedForTrial: true,
      onlyForSuperAdmin: false,
    },
    {
      label: t('drawer.dashboard'),
      path: '/dashboard',
      icon: <DashboardIcon />,
      allowedForFree: false,
      allowedForTrial: false,
      onlyForSuperAdmin: false,
    },
    {
      label: t('drawer.applicants'),
      path: '/applicants',
      icon: <ApplicantsIcon />,
      allowedForFree: true,
      allowedForTrial: true,
      onlyForSuperAdmin: false,
    },
    {
      label: t('drawer.capabilities'),
      path: '/capabilities',
      icon: <CompetenciasIcon />,
      allowedForFree: false,
      allowedForTrial: false,
      onlyForSuperAdmin: false,
    },
    {
      label: t('drawer.positions'),
      path: '/positions',
      icon: <CargosIcon />,
      allowedForFree: true,
      allowedForTrial: true,
      onlyForSuperAdmin: false,
    },
    {
      label: t('drawer.disc'),
      path: '/disc',
      icon: <DiscIcon />,
      allowedForFree: false,
      allowedForTrial: true,
      onlyForSuperAdmin: false,
    },
    {
      label: t('drawer.reasoningTests'),
      path: '/reasoning-tests',
      icon: <ReasoningIcon />,
      allowedForFree: false,
      allowedForTrial: true,
      onlyForSuperAdmin: false,
    },
    {
      label: t('drawer.personalHabilities'),
      path: '/personal-habilities',
      icon: <PersonalHabilitiesIcon />,
      allowedForFree: false,
      allowedForTrial: false,
      onlyForSuperAdmin: false,
    },
    {
      label: t('drawer.salesAssessment'),
      path: '/sales-assessment',
      icon: <SalesIcon />,
      allowedForFree: false,
      allowedForTrial: false,
      onlyForSuperAdmin: false,
    },
    {
      label: t('drawer.reports'),
      path: '/reports',
      icon: <ReportsIcon />,
      allowedForFree: true,
      allowedForTrial: true,
      onlyForSuperAdmin: false,
    },
    {
      label: t('drawer.evaluations'),
      path: '/evaluations',
      icon: <AssignmentIcon />,
      allowedForFree: false,
      allowedForTrial: false,
      onlyForSuperAdmin: false,
    },
    // Assessment 2 (PLAN_ASSESSMENT2.md): solo super-admin y organización habilitada
    ...(canUseAssessment2({ user, organization: assessment2Organization }) ? [{
      label: t('drawer.assessment2'),
      path: '/assessment2',
      icon: <AssignmentIcon />,
      allowedForFree: false,
      allowedForTrial: false,
      onlyForSuperAdmin: true,
    }] : []),
    {
      label: t('drawer.reportComparison'),
      path: '/report_comparison',
      icon: <CompareArrowsIcon />,
      allowedForFree: false,
      allowedForTrial: false,
      onlyForSuperAdmin: false,
    },
    {
      label: t('drawer.cvFiltering'),
      path: '/cv_filtering',
      icon: <FilterAltIcon />,
      allowedForFree: false,
      allowedForTrial: true,
      onlyForSuperAdmin: false,
    },
    {
      label: t('drawer.configuration'),
      path: '/configuration',
      icon: <SettingsIcon />,
      allowedForFree: true,
      allowedForTrial: true,
      onlyForSuperAdmin: false,
    },
    {
      label: t('drawer.massiveRecruitment'),
      path: '/massive-recruitment',
      icon: <MassiveRecruitmentIcon />,
      allowedForFree: false,
      allowedForTrial: false,
      erpAccess: true,
    },
  ];

  const atsMenuOptions = [
    {
      label: t('ats.home.menu'),
      path: '/ats',
      icon: <HomeIcon />,
      allowedForFree: true,
      allowedForTrial: true,
      onlyForSuperAdmin: false,
    },
    {
      label: t('ats.positions.title'),
      path: '/ats/positions',
      icon: <AtsPositionsIcon />,
      allowedForFree: true,
      allowedForTrial: true,
      onlyForSuperAdmin: false,
    },
    {
      label: t('ats.candidates.title'),
      path: '/ats/candidates',
      icon: <AtsCandidatesIcon />,
      allowedForFree: true,
      allowedForTrial: true,
      onlyForSuperAdmin: false,
    },
    {
      // Ferias: agrupaciones de vacantes con página pública y QR (PLAN_FERIAS)
      label: t('ats.fairs.menu'),
      path: '/ats/fairs',
      icon: <AtsFairsIcon />,
      allowedForFree: true,
      allowedForTrial: true,
      onlyForSuperAdmin: false,
    },
    {
      label: t('ats.config.menu'),
      path: '/ats/configuration',
      icon: <SettingsIcon />,
      allowedForFree: true,
      allowedForTrial: true,
      onlyForSuperAdmin: false,
    },
  ];

  // Modo Hunting (PLAN_HUNTING_APOLLO.md §5.4) — habilitado por organización en TopBar
  const huntingMenuOptions = [
    {
      label: t('hunting.home.menu'),
      path: '/hunting',
      icon: <HomeIcon />,
      allowedForFree: true,
      allowedForTrial: true,
      onlyForSuperAdmin: false,
    },
    {
      label: t('hunting.search.menu'),
      path: '/hunting/search',
      icon: <HuntingSearchIcon />,
      allowedForFree: true,
      allowedForTrial: true,
      onlyForSuperAdmin: false,
    },
  ];

  // section: sección de la matriz de accesos (erpAccess.js) — filtra por rol
  const erpMenuOptions = [
    {
      label: t('erp.home.menu'),
      path: '/erp',
      icon: <HomeIcon />,
      allowedForFree: true,
      allowedForTrial: true,
      erpAccess: true,
      section: 'home',
    },
    {
      label: t('erp.clients.title'),
      path: '/erp/clients',
      icon: <ErpClientsIcon />,
      allowedForFree: true,
      allowedForTrial: true,
      erpAccess: true,
      section: 'clients',
    },
    {
      label: t('erp.quotations.title'),
      path: '/erp/quotations',
      icon: <ErpQuotationsIcon />,
      allowedForFree: true,
      allowedForTrial: true,
      erpAccess: true,
      section: 'quotations',
    },
    {
      label: t('erp.employees.title'),
      path: '/erp/employees',
      icon: <ErpEmployeesIcon />,
      allowedForFree: true,
      allowedForTrial: true,
      erpAccess: true,
      section: 'employees',
    },
    {
      label: t('erp.contracts.title'),
      path: '/erp/contracts',
      icon: <ErpContractsIcon />,
      allowedForFree: true,
      allowedForTrial: true,
      erpAccess: true,
      section: 'contracts',
    },
    {
      label: t('erp.export.menu'),
      path: '/erp/export',
      icon: <ErpExportIcon />,
      allowedForFree: true,
      allowedForTrial: true,
      erpAccess: true,
      section: 'export',
    },
    {
      label: t('erp.jobTitles.title'),
      path: '/erp/job-titles',
      icon: <CargosIcon />,
      allowedForFree: true,
      allowedForTrial: true,
      erpAccess: true,
      section: 'jobTitles',
    },
    {
      label: t('erp.config.menu'),
      path: '/erp/configuration',
      icon: <SettingsIcon />,
      allowedForFree: true,
      allowedForTrial: true,
      erpAccess: true,
      section: 'configuration',
    },
  ];

  // Seleccionar el array según el modo activo
  const sourceOptions =
    appMode === 'ats' ? atsMenuOptions
    : appMode === 'erp' ? erpMenuOptions
    : appMode === 'hunting' ? huntingMenuOptions
    : allMenuOptions;

  // Filtrar opciones del menú basado en el plan y rol
  const menuOptions = sourceOptions.filter(option => {
    if (option.onlyForSuperAdmin && !isSuperAdmin) return false;
    // Opciones ERP: super-admin o usuario con rol ERP
    if (option.erpAccess && !(isSuperAdmin || erpRole)) return false;
    // Roles ERP v2 (matriz de accesos): cada sección se filtra por rol
    if (option.section && !canSeeErpSection(user, erpRole, option.section)) return false;
    if (plan === 'free') return option.allowedForFree;
    return true;
  });

  const drawerWidth = isCollapsed ? drawerWidthCollapsed : drawerWidthExpanded;

  const renderMenuItem = (option) => {
    // '/ats', '/erp' y '/hunting' (inicios de modo) requieren match exacto para no quedar
    // activos en todas las rutas /ats/* y /erp/*
    const isSelected = option.path === '/ats' || option.path === '/erp' || option.path === '/hunting'
      ? location.pathname === option.path
      : location.pathname.startsWith(option.path);

    const isDisabledByTrial = plan === 'trial' && !option.allowedForTrial;

    const menuItem = (
      <ListItemButton
        selected={isSelected && !isDisabledByTrial}
        disabled={isDisabledByTrial}
        onClick={() => !isDisabledByTrial && navigate(option.path)}
        sx={{
          minHeight: 48,
          borderRadius: 3,
          mx: isCollapsed ? 1 : 1.5,
          my: 0.5,
          justifyContent: isCollapsed ? 'center' : 'initial',
          px: 2.5,
          color: '#111827',
          '& .MuiListItemIcon-root': {
            color: '#111827',
          },
          '&.Mui-selected': {
            background: 'linear-gradient(90deg, #6A5AF9 0%, #5A4AE8 100%)',
            color: '#FFFFFF',
            '& .MuiListItemIcon-root': {
              color: '#FFFFFF',
            },
          },
          '&.Mui-selected:hover': {
            background: 'linear-gradient(90deg, #5F50EF 0%, #5344DD 100%)',
          },
          '&:hover': {
            backgroundColor: 'rgba(106, 90, 249, 0.08)',
          },
          '&.Mui-disabled': {
            opacity: 0.45,
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: isCollapsed ? 0 : 3,
            justifyContent: 'center',
          }}
        >
          {option.icon}
        </ListItemIcon>
        {!isCollapsed && <ListItemText primary={option.label} />}
      </ListItemButton>
    );

    const tooltipTitle = isDisabledByTrial
      ? t('drawer.trialUpgradeTooltip')
      : isCollapsed
        ? option.label
        : '';

    if (tooltipTitle) {
      return (
        <Tooltip key={option.label} title={tooltipTitle} placement="right">
          <span style={{ display: 'block' }}>
            {menuItem}
          </span>
        </Tooltip>
      );
    }

    return menuItem;
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          overflowX: 'hidden',
          transition: 'width 0.3s ease',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'space-between',
          px: isCollapsed ? 0 : 1.5,
          py: 1,
          minHeight: 64,
          boxSizing: 'border-box',
          borderBottom: '1px solid #E5E7EB',
        }}
      >
        {!isCollapsed && (
          <Box
            component="img"
            src="/amandalogoh.png"
            alt="Amanda"
            sx={{ height: 36, width: 'auto', pl: 0.5 }}
          />
        )}
        <IconButton
          aria-label="toggle drawer"
          onClick={onToggleDrawer}
          sx={{ color: '#6B7280' }}
        >
          {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>

      <List>
        {menuOptions.map((option) => (
          <ListItem key={option.label} disablePadding sx={{ display: 'block' }}>
            {renderMenuItem(option)}
          </ListItem>
        ))}
      </List>
    </Drawer>
  );
};

export default SideDrawer;